/**
 * The two result layers of one paired compare-surfaces run (spec §4.4),
 * linked by `metadata.comparisonGroupId`: the cut & fill GLB (it carries
 * `metadata.surfaceDifference`) and the deviation POINTCLOUD (it carries
 * `metadata.deviationStats`). The deviation run's raster child is stamped
 * with the same group and the same stats, but it is a TILE and has no panel,
 * so it is never a partner.
 */
interface ResultLayer {
  id: string;
  name?: string | null;
  type?: string | null;
  metadata?: unknown;
}

const metaOf = (layer: ResultLayer) => (layer.metadata ?? {}) as Record<string, unknown>;

export const comparisonGroupOf = (layer: ResultLayer): string | undefined => {
  const id = metaOf(layer).comparisonGroupId;
  return typeof id === 'string' && id ? id : undefined;
};

const isCutFillResult = (layer: ResultLayer) => !!metaOf(layer).surfaceDifference;
const isDeviationResult = (layer: ResultLayer) => layer.type === 'POINTCLOUD' && !!metaOf(layer).deviationStats;

const comparisonPartner = <L extends ResultLayer>(layer: ResultLayer, layers: readonly L[]): L | undefined => {
  const group = comparisonGroupOf(layer);
  if (!group) return undefined;
  const wantCutFill = !isCutFillResult(layer);
  return layers.find(
    (l) =>
      l.id !== layer.id && comparisonGroupOf(l) === group && (wantCutFill ? isCutFillResult(l) : isDeviationResult(l)),
  );
};

/** The other half of `layer`'s paired run, when that half exists. */
export function comparisonPartnerId(layer: ResultLayer, layers: readonly ResultLayer[]): string | undefined {
  return comparisonPartner(layer, layers)?.id;
}

/**
 * What a panel of a paired run asks "Create report" for (ruling R27): the
 * group, which the server turns into the run's one combined document, and
 * the cut & fill layer, whose name is the report's default title whichever
 * of the two panels was clicked.
 */
export interface ComparisonReportTarget {
  groupId: string;
  cutFillLayerId: string;
  cutFillLayerName: string;
}

/** `undefined` unless both halves of `layer`'s paired run exist. */
export function comparisonReportTarget(
  layer: ResultLayer,
  layers: readonly ResultLayer[],
): ComparisonReportTarget | undefined {
  const partner = comparisonPartner(layer, layers);
  const groupId = comparisonGroupOf(layer);
  if (!partner || !groupId) return undefined;
  const cutFill = isCutFillResult(layer) ? layer : partner;
  return { groupId, cutFillLayerId: cutFill.id, cutFillLayerName: cutFill.name ?? '' };
}

/**
 * The panel order of the analysis column: a cut & fill panel whose deviation
 * half is also shown moves to directly before it (`leadBy`, keyed by the
 * deviation layer id); every other cut & fill panel stays where it was
 * (`rest`).
 */
export function stackCutFillFirst<D extends ResultLayer, C extends ResultLayer>(
  deviation: readonly D[],
  cutFill: readonly C[],
): { leadBy: Map<string, C[]>; rest: C[] } {
  const devByGroup = new Map<string, string>();
  for (const d of deviation) {
    const g = comparisonGroupOf(d);
    if (g && !devByGroup.has(g)) devByGroup.set(g, d.id);
  }
  const leadBy = new Map<string, C[]>();
  const rest: C[] = [];
  for (const c of cutFill) {
    const g = comparisonGroupOf(c);
    const devId = g ? devByGroup.get(g) : undefined;
    if (devId) leadBy.set(devId, [...(leadBy.get(devId) ?? []), c]);
    else rest.push(c);
  }
  return { leadBy, rest };
}
